import { CDN_URL } from '../js/config.js';
import { IndexedDBService } from './IndexedDBService.js';
import { storage } from '../js/utils/storage.js';
import { loadingCoordinator } from '../core/events/index.js';
import { locationIsNsfw } from '../js/utils/nsfwTags.js';
import { getNsfwEnabled } from '../js/utils/userPrefs.js';

// Maps integer tag IDs to names from CDN: /data/characters/tags/{lang}.json
export class TagMappingService {
  constructor() {
    this.dbService = new IndexedDBService();
    this.initialized = false;
    this.tagMappings = new Map();
    this.loadingPromises = new Map();
    
    // Static mappings cache
    this.staticMappings = new Map();
    this.staticLoadingPromises = new Map();
    this.essentialsReady = loadingCoordinator.essentialsLoaded === true;
    this.lastTagMappingsPrewarmLanguage = null;
    this.lastScenarioTagsPrewarmLanguage = null;
    this._handleEssentialsReadyBound = this._handleEssentialsReady.bind(this);
    this._hasEssentialsReadyListener = false;
    
    document.addEventListener('languageChanged', (e) => {
      if (e.detail?.language) {
        this.handleLanguageChange(e.detail.language);
      }
    });

    if (!this.essentialsReady) {
      document.addEventListener('app:essentials-ready', this._handleEssentialsReadyBound);
      this._hasEssentialsReadyListener = true;
    } else {
      this._prewarmDeferredMappingsWhenEssentialsReady();
    }
  }

  async init() {
    if (this.initialized) return;
    await this.dbService.init();
    this.initialized = true;
  }

  getCurrentLanguage() {
    return storage.get('displayLanguage') || 'en';
  }

  async loadTagMappings(language = null) {
    const lang = language || this.getCurrentLanguage();
    
    if (this.tagMappings.has(lang)) return this.tagMappings.get(lang);
    if (this.loadingPromises.has(lang)) return this.loadingPromises.get(lang);

    const loadingPromise = this._fetchTagMappings(lang);
    this.loadingPromises.set(lang, loadingPromise);

    try {
      const mappings = await loadingPromise;
      this.tagMappings.set(lang, mappings);
      return mappings;
    } catch (error) {
      console.error(`Failed to load tag mappings for ${lang}:`, error);
      if (lang !== 'en') return this.loadTagMappings('en');
      return new Map();
    } finally {
      this.loadingPromises.delete(lang);
    }
  }

  async _fetchTagMappings(language) {
    await this.init();
    
    const storeName = this.dbService.stores.tagMappings || 'tag-mappings';
    const cacheKey = `tags-${language}`;
    const url = `${CDN_URL}/data/characters/tags/${language}.json`;
    
    // Check cache first by getting all data and finding our language
    try {
      const cachedData = await this.dbService.getAllData(storeName);
      const cachedItem = cachedData?.find(item => item.key === cacheKey);
      if (cachedItem && !await this.dbService.isDataStale(storeName)) {
        return this._createTagMap(cachedItem.data);
      }
    } catch (error) {
      // Cache miss or error, continue to fetch
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      
      // Store with the correct format for IndexedDBService
      await this.dbService.storeData(storeName, [{ key: cacheKey, data }]);
      return this._createTagMap(data);
    } catch (error) {
      // Try to get any cached data even if stale
      try {
        const cachedData = await this.dbService.getAllData(storeName);
        const cachedItem = cachedData?.find(item => item.key === cacheKey);
        if (cachedItem) return this._createTagMap(cachedItem.data);
      } catch {}
      throw error;
    }
  }

  _createTagMap(data) {
    const tagMap = new Map();
    
    if (Array.isArray(data)) {
      // Handle scenario tags (array of strings) or structured tags (array of objects)
      data.forEach((tag, index) => {
        if (typeof tag === 'string') {
          // Scenario tags: use the tag itself as both key and value
          tagMap.set(tag, tag);
        } else if (tag.id !== undefined && tag.name) {
          // Structured tags: use id as key, name as value
          tagMap.set(String(tag.id), tag.name);
        }
      });
    } else if (typeof data === 'object' && data !== null) {
      const nsfwAllowed = getNsfwEnabled();
      Object.entries(data).forEach(([id, name]) => {
        if (name && typeof name === 'string') {
          tagMap.set(String(id), name);
          return;
        }
        if (name && typeof name === 'object' && name.name) {
          if (!nsfwAllowed && locationIsNsfw({ id, ...name })) return;
          tagMap.set(String(id), name.name);
        }
      });
    }
    
    return tagMap;
  }

  /**
   * Parse scenario tag catalog into Map(id -> { label, tooltip, slug }).
   * Supports:
   * - id-keyed: { "1": { label, tooltip } }
   * - legacy name->tooltip: { "AnyPOV": "..." }
   * - legacy string array
   */
  _parseScenarioTagsData(data) {
    const map = new Map();
    if (Array.isArray(data)) {
      data.forEach((tag, index) => {
        if (typeof tag === 'string') {
          const id = String(index + 1);
          map.set(id, { label: tag, tooltip: '', slug: tag });
        } else if (tag && tag.id !== undefined) {
          const label = tag.label || tag.name || String(tag.id);
          map.set(String(tag.id), { label, tooltip: tag.tooltip || '', slug: label });
        }
      });
      return map;
    }
    if (typeof data !== 'object' || data === null) return map;

    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const label = value.label || value.name || key;
        map.set(String(key), {
          label,
          tooltip: value.tooltip || '',
          slug: value.slug || label
        });
        return;
      }
      // Legacy name -> tooltip
      if (typeof value === 'string') {
        map.set(String(key), { label: key, tooltip: value, slug: key });
      }
    });
    return map;
  }

  // Helper method to map arrays of IDs to names with consistent error handling
  _mapIdsWithMapping(idArray, mapping, context = '') {
    if (!Array.isArray(idArray) || idArray.length === 0) return [];
    
    const result = [];
    idArray.forEach(id => {
      const name = mapping.get(String(id));
      if (name) {
        result.push({ id, name });
      } else {
        const contextStr = context ? ` ${context}` : '';
        console.warn(`[TagMappingService] Failed to map${contextStr} ID ${id} - tag will be hidden`);
      }
    });
    
    return result;
  }

  _logMappingErrors(idArray, context = '') {
    idArray.forEach(id => {
      const contextStr = context ? ` ${context}` : '';
      console.warn(`[TagMappingService] Failed to map${contextStr} ID ${id} due to error - tag will be hidden`);
    });
  }

  async mapTagIds(tagIds, language = null) {
    if (!Array.isArray(tagIds) || tagIds.length === 0) return [];

    try {
      const tagMappings = await this.loadTagMappings(language);
      return this._mapIdsWithMapping(tagIds, tagMappings, 'tag');
    } catch (error) {
      console.error('Error mapping tag IDs:', error);
      this._logMappingErrors(tagIds, 'tag');
      return [];
    }
  }

  clearCache(language = null) {
    if (language) {
      this.tagMappings.delete(language);
      this.loadingPromises.delete(language);
    } else {
      this.tagMappings.clear();
      this.loadingPromises.clear();
    }
  }

  async preloadCurrentLanguage() {
    try {
      await this.loadTagMappings(this.getCurrentLanguage());
    } catch (error) {
      console.warn('Failed to preload tag mappings:', error);
    }
  }

  async loadStaticMapping(type) {
    const lang = this.getCurrentLanguage();
    const cacheKey = `${type}:${lang}`;

    if (this.staticMappings.has(cacheKey)) return this.staticMappings.get(cacheKey);
    if (this.staticLoadingPromises.has(cacheKey)) return this.staticLoadingPromises.get(cacheKey);

    const loadingPromise = this._fetchStaticMapping(type);
    this.staticLoadingPromises.set(cacheKey, loadingPromise);

    try {
      const mapping = await loadingPromise;
      this.staticMappings.set(cacheKey, mapping);
      return mapping;
    } catch (error) {
      console.error(`Failed to load mapping for ${type} (${lang}):`, error);
      return new Map();
    } finally {
      this.staticLoadingPromises.delete(cacheKey);
    }
  }

  async _fetchStaticMapping(type) {
    const lang = this.getCurrentLanguage();
    const mappingConfigs = {
      scenarioTags: {
        url: `${CDN_URL}/data/scenarios/tags/${lang}.json`,
        fallbackUrl: `${CDN_URL}/data/scenarios/tags/en.json`,
        localStorageKey: `mappingScenarioTags_${lang}`
      }
    };

    const config = mappingConfigs[type];
    if (!config) throw new Error(`Unknown mapping type: ${type}`);

    this._clearDifferentLanguageMappings(type, lang);

    try {
      const cachedData = storage.get(config.localStorageKey);
      if (cachedData?.timestamp && Date.now() - cachedData.timestamp < 3600000) {
        return this._parseScenarioTagsData(cachedData.data);
      }
    } catch (error) {
      console.warn(`Failed to load ${type} from localStorage:`, error);
    }

    try {
      const response = await fetch(config.url);
      if (!response.ok) {
        if (config.fallbackUrl && lang !== 'en') {
          console.warn(`Failed to fetch ${type} for ${lang}, trying English fallback`);
          const fallbackResponse = await fetch(config.fallbackUrl);
          if (!fallbackResponse.ok) {
            throw new Error(`HTTP ${fallbackResponse.status} (fallback)`);
          }
          const data = await fallbackResponse.json();

          try {
            storage.set(config.localStorageKey, {
              data,
              timestamp: Date.now()
            });
          } catch (error) {
            console.warn(`Failed to cache ${type} to storage:`, error);
          }

          return this._parseScenarioTagsData(data);
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      try {
        storage.set(config.localStorageKey, {
          data,
          timestamp: Date.now()
        });
      } catch (error) {
        console.warn(`Failed to cache ${type} to storage:`, error);
      }

      return this._parseScenarioTagsData(data);
    } catch (error) {
      console.error(`Error fetching mapping ${type}:`, error);
      throw error;
    }
  }

  _handleEssentialsReady() {
    this.essentialsReady = true;

    if (this._hasEssentialsReadyListener) {
      document.removeEventListener('app:essentials-ready', this._handleEssentialsReadyBound);
      this._hasEssentialsReadyListener = false;
    }

    this._prewarmDeferredMappingsWhenEssentialsReady();
  }

  _prewarmDeferredMappingsWhenEssentialsReady() {
    if (!this.essentialsReady) return;

    const language = this.getCurrentLanguage();
    if (this.lastTagMappingsPrewarmLanguage !== language) {
      this.lastTagMappingsPrewarmLanguage = language;
      this.loadTagMappings(language).catch((error) => {
        console.warn(`[TagMappingService] Failed to prewarm tag mappings for ${language}:`, error);
        if (this.lastTagMappingsPrewarmLanguage === language) {
          this.lastTagMappingsPrewarmLanguage = null;
        }
      });
    }

    if (this.lastScenarioTagsPrewarmLanguage !== language) {
      this.lastScenarioTagsPrewarmLanguage = language;
      this.loadStaticMapping('scenarioTags').catch((error) => {
        console.warn('[TagMappingService] Failed to prewarm scenario tags:', error);
        if (this.lastScenarioTagsPrewarmLanguage === language) {
          this.lastScenarioTagsPrewarmLanguage = null;
        }
      });
    }
  }

  _getScenarioTagsMapping() {
    const lang = this.getCurrentLanguage();
    return this.staticMappings.get(`scenarioTags:${lang}`)
      || this.staticMappings.get('scenarioTags:en')
      || null;
  }

  /**
   * @returns {Promise<Array<{ id: number, label: string, tooltip: string, slug: string }>>}
   */
  async getAllScenarioTagOptions() {
    try {
      const mapping = await this.loadStaticMapping('scenarioTags');
      const options = Array.from(mapping.entries()).map(([id, meta]) => {
        if (meta && typeof meta === 'object' && meta.label) {
          return {
            id: Number(id),
            label: meta.label,
            tooltip: meta.tooltip || '',
            slug: meta.slug || meta.label
          };
        }
        // Legacy map value is tooltip or label string
        const label = typeof meta === 'string' && meta !== id ? String(id) : String(meta || id);
        const tooltip = typeof meta === 'string' && meta !== id ? meta : '';
        return { id: Number(id), label, tooltip, slug: label };
      }).filter((opt) => Number.isFinite(opt.id));

      options.sort((a, b) => a.label.localeCompare(b.label));
      return options;
    } catch (error) {
      console.error('Error loading scenario tags:', error);
      return [];
    }
  }

  getScenarioTagTooltip(tagIdOrLabel) {
    const mapping = this._getScenarioTagsMapping();
    if (!mapping) return '';

    const byId = mapping.get(String(tagIdOrLabel));
    if (byId && typeof byId === 'object') return byId.tooltip || '';
    if (typeof byId === 'string' && byId !== tagIdOrLabel) return byId;

    for (const meta of mapping.values()) {
      if (!meta || typeof meta !== 'object') continue;
      if (meta.label === tagIdOrLabel || meta.slug === tagIdOrLabel) return meta.tooltip || '';
    }
    return '';
  }

  getScenarioTagLabel(tagId) {
    const mapping = this._getScenarioTagsMapping();
    if (!mapping) return '';
    const meta = mapping.get(String(tagId));
    if (meta && typeof meta === 'object') return meta.label || '';
    if (typeof meta === 'string') return meta;
    return '';
  }

  /**
   * Resolve mixed ids/labels/slugs to numeric tag ids using the loaded catalog.
   * @param {Array<number|string>} values
   * @returns {number[]}
   */
  resolveScenarioTagIds(values) {
    if (!Array.isArray(values) || values.length === 0) return [];
    const mapping = this._getScenarioTagsMapping();
    if (!mapping) {
      return values.map((v) => Number(v)).filter((id) => Number.isFinite(id));
    }

    const slugToId = new Map();
    for (const [id, meta] of mapping.entries()) {
      if (meta && typeof meta === 'object') {
        slugToId.set(String(meta.slug || '').toLowerCase(), Number(id));
        slugToId.set(String(meta.label || '').toLowerCase(), Number(id));
      } else if (typeof meta === 'string') {
        slugToId.set(String(id).toLowerCase(), Number(id));
      }
    }

    const aliasToSlug = {
      tentacles: 'Tentacles',
      tantacles: 'Tentacles',
      safe: 'SFW',
      sfw: 'SFW',
      romance: 'Romance',
      romantic: 'Romance',
      rivalry: 'Rivalry',
      taboo: 'Taboo',
      submission: 'Submissive',
      submissive: 'Submissive',
      domination: 'Dominant',
      dominant: 'Dominant',
      hypnosis: 'Hypnosis',
      blackmail: 'Blackmail',
      humiliation: 'Humiliation',
      voyeurism: 'Voyeurism',
      teasing: 'Teasing',
      fluff: 'Fluff',
      comedy: 'Comedy',
      friendship: 'Friendship',
      anypov: 'AnyPOV',
      'male pov': 'Male POV',
      'female pov': 'Female POV',
      'dead dove': 'Dead dove',
      'ennemies to lovers': 'Ennemies to lovers',
      'enemies to lovers': 'Ennemies to lovers',
      'school-life': 'school-life',
      'school life': 'school-life',
      yandere: 'yandere',
      tsundere: 'tsundere',
      smut: 'Smut',
      nsfw: 'Smut',
      ntr: 'NTR',
      rpg: 'RPG',
      yaoi: 'Yaoi',
      yuri: 'Yuri',
      harem: 'Harem',
      incest: 'Incest',
      revenge: 'Revenge',
      switch: 'Switch',
      brat: 'Brat',
      bully: 'Bully',
      assistant: 'Assistant'
    };

    const seen = new Set();
    const ids = [];
    values.forEach((value) => {
      const asNum = Number(value);
      if (Number.isFinite(asNum) && mapping.has(String(asNum))) {
        if (!seen.has(asNum)) {
          seen.add(asNum);
          ids.push(asNum);
        }
        return;
      }
      const raw = String(value || '').trim().toLowerCase();
      const aliasSlug = aliasToSlug[raw];
      const resolved = slugToId.get(raw)
        || (aliasSlug ? slugToId.get(String(aliasSlug).toLowerCase()) : undefined);
      if (Number.isFinite(resolved) && !seen.has(resolved)) {
        seen.add(resolved);
        ids.push(resolved);
      }
    });
    return ids;
  }

  _clearDifferentLanguageMappings(type, currentLang) {
    const keyPatterns = {
      scenarioTags: 'mappingScenarioTags_'
    };

    const keyPattern = keyPatterns[type];
    if (!keyPattern) return;

    // Clear in-memory cache for different languages
    const keysToDelete = [];
    for (const key of this.staticMappings.keys()) {
      if (key.startsWith(`${type}:`)) {
        const [, lang] = key.split(':');
        if (lang !== currentLang) {
          keysToDelete.push(key);
        }
      }
    }
    keysToDelete.forEach(key => {
      this.staticMappings.delete(key);
      this.staticLoadingPromises.delete(key);
    });

    const currentKey = `${keyPattern}${currentLang}`;
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(keyPattern) && key !== currentKey) {
        storage.remove(key);
      }
    });
  }

  clearStaticCache(type = null, language = null, clearLocalStorage = false) {
    if (type) {
      if (language) {
        const cacheKey = `${type}:${language}`;
        this.staticMappings.delete(cacheKey);
        this.staticLoadingPromises.delete(cacheKey);

        if (clearLocalStorage && type === 'scenarioTags') {
          storage.remove(`mappingScenarioTags_${language}`);
        }
      } else {
        const keysToDelete = [];
        for (const key of this.staticMappings.keys()) {
          if (key === type || key.startsWith(`${type}:`)) {
            keysToDelete.push(key);
          }
        }
        keysToDelete.forEach(key => {
          this.staticMappings.delete(key);
          this.staticLoadingPromises.delete(key);
        });

        if (clearLocalStorage && type === 'scenarioTags') {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('mappingScenarioTags_') || key === 'mappingScenarioTags') {
              storage.remove(key);
            }
          });
        }
      }
    } else {
      this.staticMappings.clear();
      this.staticLoadingPromises.clear();

      if (clearLocalStorage) {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('mappingScenarioTags_') || key === 'mappingScenarioTags') {
            storage.remove(key);
          }
        });
      }
    }
  }

  async handleLanguageChange(newLanguage) {
    console.log(`[TagMappingService] Language changed to: ${newLanguage}`);

    const oldLanguage = this.getCurrentLanguage();
    if (oldLanguage !== newLanguage) {
      this.clearCache(oldLanguage);
      this.clearStaticCache('scenarioTags', oldLanguage, true);
    }

    try {
      this._prewarmDeferredMappingsWhenEssentialsReady();

      console.log(`[TagMappingService] Successfully loaded mappings for ${newLanguage}`);

      document.dispatchEvent(new CustomEvent('mappingsUpdated', {
        detail: { language: newLanguage }
      }));
    } catch (error) {
      console.error(`[TagMappingService] Failed to load mappings for ${newLanguage}:`, error);
    }
  }
}

export const tagMappingService = new TagMappingService();
